import { useContext, useRef } from "react";

import { useTranslation } from "react-i18next";
import { CSSTransition } from "react-transition-group";

import { css } from "@emotion/react";

import { BannerType, BannerView } from "@dotnet/telematics-design-system";

import Button, {
    BUTTON_COLORS,
    BUTTON_SIZES,
    BUTTON_VARIANTS
} from "components/DesignComponents/Button";
import InputField, {
    INPUT_FIELD_SIZES
} from "components/DesignComponents/InputField";
import AuthContext from "context/auth/authContext";
import { isAuthenticatorType } from "shared";

import AuthContainer from "../AuthContainer";
import Redirection from "../Redirection/Redirection";
import { OtpInputField } from "../shared";
import { INPUT_FIELD } from "../types";
import BackButton from "./BackButton";
import ForgotPasswordLink from "./ForgotPasswordLink";
import { useLogin, useTwoFactorVerification } from "./hooks";
import RecoveryCodeLink from "./RecoveryCodeLink";
import SecondaryText from "./SecondaryText";
import SingleSignOn from "./SingleSignOn";

const Login = () => {
    const { hasSso, is2FaVerification, isLoading, authType } =
        useContext(AuthContext);

    const { t } = useTranslation();

    const {
        otpCode,
        recoveryCode,
        twoFaError,
        isRecoveryCodePhase,
        handleRecoveryCodePhase,
        handleOtpChange,
        handleRecoveryCodeChange,
        handle2FaSubmit,
        resetOtpError
    } = useTwoFactorVerification();

    const {
        message,
        isEmailValid,
        emailError,
        passwordError,
        showPassword,
        handleLoginSubmit,
        validateEmail,
        handleLoginChange,
        PasswordVisibility
    } = useLogin();

    const nodeRef = useRef(null);

    const getFormName = () => {
        if (is2FaVerification) {
            return isRecoveryCodePhase
                ? t("Auth##account verification##recovery code##title")
                : t("Auth##account verification##title");
        }

        return t("Auth##sign in to fota");
    };

    return (
        <Redirection>
            <AuthContainer
                formName={getFormName()}
                secondaryText={
                    <SecondaryText isRecoveryCodePhase={isRecoveryCodePhase} />
                }
                topButton={
                    is2FaVerification ? (
                        <BackButton
                            isRecoveryCodePhase={isRecoveryCodePhase}
                            handleRecoveryCodePhase={handleRecoveryCodePhase}
                            resetOtpError={resetOtpError}
                        />
                    ) : undefined
                }
            >
                <CSSTransition
                    nodeRef={nodeRef}
                    in={Boolean(message?.title)}
                    timeout={{ enter: 300, exit: 0 }}
                    classNames="auth-error-message"
                    unmountOnExit
                >
                    <div
                        ref={nodeRef}
                        css={css({
                            marginBottom: "16px",

                            "&.auth-error-message-enter": {
                                opacity: "0"
                            },

                            "&.auth-error-message-enter-active": {
                                opacity: "1",
                                transition: "opacity 300ms ease-in"
                            },

                            "&.auth-error-message-exit, &.auth-error-message-exit-active":
                                { opacity: "0" }
                        })}
                    >
                        <BannerView
                            bannerType={message?.status ?? BannerType.Error}
                            title={t(`Flash##${message?.title}`, {
                                defaultValue: message?.title
                            })}
                            description={message?.description}
                        />
                    </div>
                </CSSTransition>

                {isRecoveryCodePhase && (
                    <BannerView
                        bannerType={BannerType.Info}
                        title={t(
                            "Auth##account verification##recovery code##banner text"
                        )}
                        css={css({
                            marginBottom: "24px"
                        })}
                    />
                )}

                <form
                    noValidate
                    onSubmit={
                        is2FaVerification ? handle2FaSubmit : handleLoginSubmit
                    }
                >
                    {is2FaVerification ? (
                        <>
                            {isRecoveryCodePhase ? (
                                <InputField
                                    autoFocus
                                    fullWidth
                                    readOnly={isLoading}
                                    errorText={twoFaError}
                                    labelLeft={t(
                                        "Security##recovery codes##input field title"
                                    )}
                                    placeholder={t("Dialog##enter")}
                                    size={INPUT_FIELD_SIZES.Medium}
                                    onChange={e =>
                                        handleRecoveryCodeChange(e.target.value)
                                    }
                                />
                            ) : (
                                <OtpInputField
                                    isLoading={isLoading}
                                    error={twoFaError}
                                    onChange={handleOtpChange}
                                    label={
                                        isAuthenticatorType(authType)
                                            ? undefined
                                            : t(
                                                  "Auth##account verification##code sent to email"
                                              )
                                    }
                                />
                            )}

                            <Button
                                fullWidth
                                color={BUTTON_COLORS.Primary}
                                size={BUTTON_SIZES.Normal}
                                variant={BUTTON_VARIANTS.TextOnly}
                                type="submit"
                                disabled={
                                    isRecoveryCodePhase
                                        ? recoveryCode.length < 6
                                        : otpCode.length < 6
                                }
                                isLoading={isLoading}
                                css={css({
                                    marginTop: "16px"
                                })}
                            >
                                {t("Button##verify")}
                            </Button>

                            {isAuthenticatorType(authType) &&
                                !isRecoveryCodePhase && (
                                    <RecoveryCodeLink
                                        onClick={handleRecoveryCodePhase}
                                    />
                                )}
                        </>
                    ) : (
                        <>
                            <InputField
                                data-testid="login-email-input-field"
                                size={INPUT_FIELD_SIZES.Medium}
                                id="standard-error-helper-text"
                                fullWidth
                                name={INPUT_FIELD.Email}
                                onBlur={validateEmail}
                                readOnly={isLoading}
                                errorText={isEmailValid ? "" : emailError}
                                onChange={handleLoginChange}
                                labelLeft={t("Dialog##email")}
                                placeholder={`${t("Dialog##example")} email@fota.com`}
                                customStyle={{ marginBottom: "16px" }}
                            />

                            <InputField
                                fullWidth
                                name={INPUT_FIELD.Password}
                                labelLeft={t("Auth##password")}
                                placeholder={t("General##enter here")}
                                size={INPUT_FIELD_SIZES.Medium}
                                type={
                                    showPassword.current ? "text" : "password"
                                }
                                readOnly={isLoading}
                                onChange={handleLoginChange}
                                errorText={passwordError.password1}
                                iconRight={<PasswordVisibility />}
                            />

                            <ForgotPasswordLink />

                            <Button
                                data-testid="sign-in-button"
                                fullWidth
                                color={BUTTON_COLORS.Primary}
                                size={BUTTON_SIZES.Normal}
                                variant={BUTTON_VARIANTS.TextOnly}
                                type="submit"
                                isLoading={isLoading}
                            >
                                {t("Auth##sign in")}
                            </Button>

                            {hasSso && <SingleSignOn />}
                        </>
                    )}
                </form>
            </AuthContainer>
        </Redirection>
    );
};

export default Login;
